import Alpine from 'alpinejs'

import { productVariants } from "../gql/_productQueries"
import { formatCurrency } from "../utilities/_commerce.js"

Alpine.store("cart", {
	totalQty: 0,
	totalPrice: 0,
	itemTotal: 0,
	itemSubtotalAsCurrency: '',
	itemTotalAsCurrency: '',
	totalDiscountAsCurrency: '',
	lineItems: [],
	addedToCart: false,
	addedItem: null,
	addToCartTimeout: null,
	isUpdatingLineItem: false,

	init() {
		const addedToCartEl = document.querySelector('.header__cart__added')

		if (addedToCartEl !== null) {
			Alpine.effect(() => {
				if (Alpine.store('cart').addedToCart) {
					addedToCartEl.focus()
				}
			})
		}
	},

	async add(id, title, price, qty, url, variantOptionsString) {
		let postData = {
			purchasables: [
				{
					id: id,
					qty: qty,
					options: {
						title: title,
						mainProductUrl: url,
						variantOptionsString: variantOptionsString
					}
				}
			]
		}

		const response = await fetch('/actions/commerce/cart/update-cart', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'X-Requested-With': 'XMLHttpRequest',
				'X-CSRF-TOKEN': window.csrfTokenValue
			},
			body: JSON.stringify(postData)
		})
		const data = await response.json()

		this.lineItems = data.cart.lineItems

		this.totalQty = data.cart.totalQty
		this.totalPrice = data.cart.itemTotal
		this.itemSubtotalAsCurrency = data.cart.itemSubtotalAsCurrency
		this.itemTotalAsCurrency = data.cart.itemTotalAsCurrency
		this.totalDiscountAsCurrency = data.cart.totalDiscountAsCurrency

		this.addedItem = {
			title: title,
			qty: qty,
			price: formatCurrency(price * qty),
			variantOptionsString: variantOptionsString,
			imageJpg1x: await this.getThumbnail(id, "jpg"),
			imageWebp1x: await this.getThumbnail(id, "webp"),
		}

		this.addedToCart = true

		if (this.addToCartTimeout) {
			clearTimeout(this.addToCartTimeout)
		}

		this.addToCartTimeout = setTimeout(() => {
			this.addedToCart = false

			setTimeout(() => {
				this.addedItem = null
			}, 500)
		}, 4000)
	},

	async remove(id) {
		let postData = {
			lineItems: { [id]: { remove: true } },
		}
		const response = await fetch('/actions/commerce/cart/update-cart', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Accept: 'application/json',
				'X-Requested-With': 'XMLHttpRequest',
				'X-CSRF-TOKEN': window.csrfTokenValue
			},
			body: JSON.stringify(postData)
		})
		const data = await response.json()

		this.lineItems = data.cart.lineItems
		this.totalQty = data.cart.totalQty
		this.totalPrice = data.cart.itemTotal
		this.itemSubtotalAsCurrency = data.cart.itemSubtotalAsCurrency
		this.itemTotalAsCurrency = data.cart.itemTotalAsCurrency
		this.totalDiscountAsCurrency = data.cart.totalDiscountAsCurrency
	},

	async update(id, qty) {
		this.isUpdatingLineItem = true

		let postData = {
			lineItems: { [id]: { qty: qty } },
		}

		try {
			const response = await fetch('/actions/commerce/cart/update-cart', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					'X-Requested-With': 'XMLHttpRequest',
					'X-CSRF-TOKEN': window.csrfTokenValue
				},
				body: JSON.stringify(postData)
			})
			const data = await response.json()

			this.lineItems = data.cart.lineItems
			this.totalQty = data.cart.totalQty
			this.totalPrice = data.cart.itemTotal
			this.itemSubtotalAsCurrency = data.cart.itemSubtotalAsCurrency
			this.itemTotalAsCurrency = data.cart.itemTotalAsCurrency
			this.totalDiscountAsCurrency = data.cart.totalDiscountAsCurrency

			window.dispatchEvent(new CustomEvent('lineItemUpdated', {
				detail: {
					id,
					qty
				}
			}))
		} catch (err) {
			console.error('Error updating cart:', err)
		} finally {
			this.isUpdatingLineItem = false
		}
	},

	async getCart() {
		try {
			const response = await fetch('/actions/commerce/cart/get-cart', {
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
					'X-Requested-With': 'XMLHttpRequest',
					'X-CSRF-TOKEN': window.csrfTokenValue
				}
			})
			const data = await response.json()

			this.lineItems = data.cart.lineItems

			this.totalQty = data.cart.totalQty
			this.totalPrice = data.cart.itemTotal
			this.itemSubtotalAsCurrency = data.cart.itemSubtotalAsCurrency
			this.itemTotalAsCurrency = data.cart.itemTotalAsCurrency
			this.totalDiscountAsCurrency = data.cart.totalDiscountAsCurrency

			window.dispatchEvent(new CustomEvent('cartUpdated'))
		} catch (err) {
			console.error('Error updating cart:', err)
		}
	},

	async getThumbnail(id, type = "jpg") {
		const response = await fetch("/api", {
				method: "POST",
				credentials: 'same-origin',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					query: productVariants,
					variables: {
						limit: 1,
						id: id,
					},
				}),
			})

		const json = await response.json()
		const data = json.data

		if (data.variants.length === 0) {
			return null
		}

		const jpgUrl = data.variants[0].imageJpg1x[0].url
		const webpUrl = data.variants[0].imageWebp1x[0].url

		if (type === "jpg") {
			return jpgUrl
		} else if (type === "webp") {
			return webpUrl
		}
	},

	getCategoryIcon(category) {
		if (!category) return null

		if (category.toLowerCase().includes('table')) {
			return '/dist/images/icons/product-categories/icon--table.svg'
		} else if (category.toLowerCase().includes('seating')) {
			return '/dist/images/icons/product-categories/icon--seating.svg'
		} else if (category.toLowerCase().includes('cupboard')) {
			return '/dist/images/icons/product-categories/icon--cupboard.svg'
		} else if (category.toLowerCase().includes('rack')) {
			return '/dist/images/icons/product-categories/icon--rack.svg'
		} else if (category.toLowerCase().includes('misc')) {
			return '/dist/images/icons/product-categories/icon--misc.svg'
		}
	},

	clear() {
		this.lineItems = []
	},
})

Alpine.store("cart").getCart()
